import { cn } from '@herit-fe/heritage-ui';

export interface PowerBarProps {
  /** 출력 비율 % (0~100+). 음수/결측은 호출처가 0 으로 넘긴다. */
  ratio: number;
  /** 트랙 크기·모양 override(예: 카드 안 `w-full`). twMerge 로 기본 폭/높이를 덮는다. */
  className?: string;
}

/**
 * 채움 그라데이션 색 — **단일 hue(green) 순차 램프**(light→dark)를 좌→우로 연속 보간. 값이 클수록 진한 구간까지
 * 드러난다. CSS 변수로 둔 건 `dark:` 변형으로 두 stop 을 한 번에 갈아끼우기 위함.
 */
const GRADIENT_VARS =
  '[--gauge-from:var(--color-green-300)] [--gauge-to:var(--color-green-500)] ' +
  'dark:[--gauge-from:var(--color-green-300)] dark:[--gauge-to:var(--color-green-400)]';

/**
 * 출력 비율 **가로 배터리 게이지** — 트랙(회색) 위에 채움 창(`width: ratio%`)을 얹고, 그 창이 **트랙 전체 폭에
 * 깔린 그라데이션을 크롭**해 보여준다. 좌(연함)→우(진함) 단일 hue 램프가 **위치에 고정**되므로 채움이 짧아도
 * 진한 끝색이 드러나지 않는다("많이 참" 오독 방지). 100% 초과는 채움을 100 에서 멈춘다.
 *
 * 3층 구조인 이유 — 채움 창이 **자체 radius + overflow-hidden** 을 가져야 채움의 오른쪽 끝이 둥글게 떨어진다
 * (예전처럼 우측을 트랙색 사각형으로 덮으면 경계가 직각으로 잘림). 그런데 창을 positioned 로 만들면 그 안의
 * `inset-0` 그라데이션이 **창 크기**로 잡혀 색이 채움 폭을 따라 늘어나고, 반대로 창을 static 으로 두면
 * `position:absolute` 가 non-positioned 조상의 overflow 를 통과해 **크롭 자체가 안 걸린다**. 그래서 창은
 * positioned 로 두고 그라데이션 폭만 컨테이너 쿼리 단위(`100cqw` = 트랙 폭)로 떼어낸다 — 트랙의 `@container`
 * 선언이 그 기준점이라 **둘은 세트**(빼면 cqw 가 뷰포트로 폴백해 그라데이션이 터진다).
 *
 * 폭은 `className`(twMerge)으로 조절 — 기본은 인라인 병기용 `w-7`, 카드처럼 넓게 쓸 땐 `w-full`.
 * 인접 텍스트가 이미 "NN%" 를 읽어주므로 `aria-hidden`(중복 낭독 방지).
 */
export function PowerBar({ ratio, className }: PowerBarProps) {
  const clamped = Math.min(Math.max(ratio, 0), 100);
  return (
    <span
      className={cn(
        '@container relative inline-block h-2.5 w-7 shrink-0 overflow-hidden rounded-full bg-border align-middle',
        GRADIENT_VARS,
        className,
      )}
      aria-hidden="true"
    >
      {/* 채움 창 — 폭만 비율로, 양 끝 radius 는 트랙과 동일(좌측은 트랙과 겹쳐 무해, 우측이 실제 채움 끝).
          모션은 둘로 나뉜다: **마운트 = 0 에서 차오름**(`animate-power-fill`, globals.css 키프레임) ·
          **값 갱신 = 폭 보간**(`transition-[width]`, 폴링으로 비율만 바뀔 때). 그라데이션은 창이 아니라 트랙에
          앵커돼 있으므로 차오르는 동안 색이 늘어나지 않고 왼쪽부터 **닦아내듯 드러난다**.
          `motion-safe:` = OS 의 "동작 줄이기" 켜면 둘 다 끄고 최종 폭으로 즉시 표시. */}
      <span
        className="absolute inset-y-0 left-0 overflow-hidden rounded-full duration-500 ease-out motion-safe:animate-power-fill motion-safe:transition-[width]"
        style={{ width: `${clamped}%` }}
      >
        {/* 트랙 전체 폭 그라데이션(색 앵커) — 창 밖은 위 overflow-hidden 이 잘라낸다. */}
        <span className="absolute inset-y-0 left-0 w-[100cqw] [background:linear-gradient(to_right,var(--gauge-from),var(--gauge-to))]" />
      </span>
    </span>
  );
}
